// ══════════════════════════════════════════════════════════════
// COMPASS – Authentication Interceptor
// ══════════════════════════════════════════════════════════════
import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AppState } from '../store/app.state';
import { UserActions } from '../store/user/user.actions';

/**
 * Functional HTTP interceptor for:
 * 1. Injecting Bearer token in outgoing requests
 * 2. Handling 401 errors by attempting token refresh (single-flight)
 * 3. Preventing infinite retry loops via X-Retry-Attempt header
 */

const RETRY_HEADER = 'X-Retry-Attempt';

function addToken<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function markRetried<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      [RETRY_HEADER]: '1',
    },
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const store = inject(Store<AppState>);

  // Skip auth for Keycloak endpoints (to avoid infinite loops)
  if (
    req.url.includes('/auth/token') ||
    req.url.includes('/auth/realms') ||
    req.url.includes('/protocol/openid-connect')
  ) {
    return next(req);
  }

  /** A second 401 after a forced refresh: this request is still refused, but that does not make the whole session
   *  dead. The session is expired only if Keycloak no longer considers the user authenticated either; otherwise
   *  only the request fails. */
  const expireUnlessAuthenticated = (error: HttpErrorResponse) => {
    if (!authService.isAuthenticated()) {
      store.dispatch(UserActions.sessionExpired());
    }
    return throwError(() => error);
  };

  return authService.ensureValidToken().pipe(
    catchError((refreshError) => {
      store.dispatch(UserActions.sessionExpired());
      return throwError(() => refreshError);
    }),
    switchMap((token) => {
      const authReq = token ? addToken(req, token) : req;

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) {
            return throwError(() => error);
          }

          if (!authService.getToken()) {
            return throwError(() => error);
          }

          // Already a retry: never refreshed and retried again. (The retry below goes to the next handler, not back
          // through this interceptor, so this only guards a request that arrives marked.)
          if (req.headers.has(RETRY_HEADER)) {
            return expireUnlessAuthenticated(error);
          }

          return authService.refreshToken(-1).pipe(
            // Only a failed refresh means the session is gone: this catch sits before the retry, so the
            // retry's own errors never reach it.
            catchError((refreshError) => {
              store.dispatch(UserActions.sessionExpired());
              return throwError(() => refreshError);
            }),
            switchMap(() => {
              const newToken = authService.getToken();

              if (!newToken) {
                store.dispatch(UserActions.sessionExpired());
                return throwError(() => error);
              }

              return next(markRetried(req, newToken)).pipe(
                catchError((retryError: unknown) =>
                  retryError instanceof HttpErrorResponse && retryError.status === 401
                    ? expireUnlessAuthenticated(retryError)
                    : throwError(() => retryError))
              );
            })
          );
        })
      );
    })
  );
};
